import { RouterView } from 'vue-router';
import { captions } from './closedCaptions';
import { linkify } from './helpers/helper';
import { metrics } from './metrics';
import { router } from './router';
import { default_channel_id, state, store_url } from './state';
import { initTheme } from './themes';
import { local_media_promise, webRTC } from './webRTC';
import { transport } from './firebaseTransport';
import { ToastList } from './components/toast';
import { Navbar } from './components/navbar';
import { Loading } from './components/loading';
import { CookieBanner } from './components/cookieBanner';
import { buildAppLaunchUrl } from './launchApp';

initTheme();

const app = Vue.createApp({
	setup() {
		return () =>
			state.hasChannel ? (
				<div>
					<RouterView />
					<CookieBanner />
				</div>
			) : (
				<div id="app">
					<Navbar />
					<RouterView />
					<ToastList />
					<Loading />
					<CookieBanner />
				</div>
			);
	}
});

app.use(router);
app.mount('#root_app');

// Note: the host page does not call gatheract.init(); that API is for module
// iframes (which receive ?app_id&channel_id&token in their URL). The host
// uses Firebase Auth + RTDB directly. state.apiInitPromise stays as the
// already-resolved Promise from state.ts.

window.onbeforeunload = function (e) {};

app.config.errorHandler = function (err, vm, info) {
	console.error(err);
};

function updateChannelUrl(channel: string) {
	if (channel === default_channel_id) return;
	const params = new URLSearchParams(location.search);
	if (location.pathname.match('^/client.*')) {
		channel = 'client/' + channel;
	}
	if (params.toString().length > 0) {
		window.history.replaceState({}, '', `${location.origin}/${channel}?${params.toString()}`);
	} else {
		window.history.replaceState({}, '', `${location.origin}/${channel}`);
	}
}

export async function onApiMessage(msg: IApiMessage) {
	if (msg.type === 'connected') {
		state.connected = true;
	} else if (msg.type === 'socket_closed') {
		state.connected = false;
		state.hangup();
	} else if (msg.type === 'join_channel_response') {
		state.channel_id = msg.channel;
		state.user = msg.user;
		updateChannelUrl(msg.channel);
		state.audioEnabled = msg.audioEnabled;
		if (state.audioEnabled) {
			//local_media_promise = webRTC.setup_local_media();
		}

		if (state.app_id) {
			const info = state.appInfo;
			api.activateApp(state.app_id, true, info && {
				name: info.name,
				url: info.launch_url,
				image: info.tile_image
			});
			state.app_id = null;
		}
		await webRTC.setAudioOptions();
	} else if (msg.type === 'channel_info') {
		if (msg.newUser) {
			state.messages.push({
				from: '[Channel]',
				text: `${msg.newUser.name} joined the channel.`,
				url: null
			});
			transport
				.broadcast({ type: 'muted', muted: state.micMuted, user: state.user })
				.catch(err => console.error('mute rebroadcast failed', err));
		}
		if (msg.leftUser) {
			state.messages.push({
				from: '[Channel]',
				text: `${msg.leftUser.name} left the channel.`,
				url: null
			});
		}

		state.updateUsers(msg.users);
	} else if (msg.type == 'post_message') {
		state.messages.push({
			from: msg.from,
			text: msg.message,
			url: linkify(msg.message)
		});
		Vue.nextTick(() => {
			let chat = document.querySelector('.chat_messages');
			chat.scrollTop = chat.scrollHeight;
		});
	} else if (msg.type == 'activate_app') {
		state.appName = msg.name;
		try {
			state.appUrl = await buildAppLaunchUrl(msg.url, msg.appId, state.channel_id);
		} catch (e) {
			console.error('failed to build app launch url', e);
			state.appUrl = msg.url; // fallback — module will fail without token
		}
		// Apps cannot run in gallery mode — drop back to speaker view.
		if (state.videoLayout === 'gallery') state.videoLayout = 'speaker';
		state.browsingApps = false;
		state.appImageUrl = store_url + msg.image;
		state.setMainVideo(null);
		Vue.nextTick(() => {
			let frame: HTMLElement = document.querySelector('#app-frame');
			frame.focus();
		});
		metrics.startApp(msg.name);
	} else if (msg.type == 'webrtc_init_connection') {
		await local_media_promise;
		webRTC.startConnection(msg.peer_id);
	} else if (msg.type == 'webrtc_offer') {
		await local_media_promise;
		webRTC.onWebRtcMessage(msg.peer_id, msg.offer, null);
	} else if (msg.type == 'webrtc_answer') {
		await local_media_promise;
		webRTC.onWebRtcMessage(msg.peer_id, msg.answer, null);
	} else if (msg.type == 'webrtc_drop_connection') {
		await local_media_promise;
		webRTC.removePeer(msg.peer_id);
	} else if (msg.type == 'webrtc_ice_candidate') {
		await local_media_promise;
		webRTC.onWebRtcMessage(msg.peer_id, null, msg.ice_candidate);
	} else if (msg.type == 'captions_enable') {
		if (msg.enable) {
			state.captions_enabled = true;
			captions.init();
		} else {
			state.captions_enabled = false;
			captions.stopForever();
		}
	} else if (msg.type == 'caption') {
		let n = '';
		state.users.forEach(u => {
			if (u.id == msg.from) {
				n = u.name;
			}
		});
		let packet: ICaptionPart = {
			from: msg.from,
			text: msg.message,
			time: Date.now(),
			name: n,
			finished: msg.finished
		};
		captions.addMessage(packet);
	} else if (msg.type == 'muted') {
		state.updateUserMutedState(msg.user, msg.muted);
	} else if (msg.type == 'close_app') {
		state.closeApp(false);
	} else if (msg.type == 'user_banned') {
		// Broadcast carries user_id targeting; only the banned user reacts.
		const targetId = (msg as any).user_id;
		if (!targetId || targetId === state.user.id) {
			state.handleUserBanned(msg.channel_id, msg.banned_user_name);
		}
	} else if (msg.type === 'raise_hand') {
		const u = state.users.find(u => u.id === (msg as any).user_id);
		if (u) u.handRaised = !!(msg as any).raised;
	} else if (msg.type === 'reaction') {
		const u = state.users.find(u => u.id === (msg as any).user_id);
		if (u && (msg as any).emoji && (msg as any).reaction_id) {
			const reaction = { emoji: (msg as any).emoji, id: (msg as any).reaction_id, ts: Date.now() };
			u.reactions.push(reaction);
			setTimeout(() => {
				const idx = u.reactions.findIndex(r => r.id === reaction.id);
				if (idx !== -1) u.reactions.splice(idx, 1);
			}, 3000);
		}
	} else if (msg.type === 'host_mute_all') {
		if (msg.from === state.channelOwner && msg.from !== state.user?.id) {
			state.enableMic(false);
		}
	} else if (msg.type === 'host_stop_all_video') {
		if (msg.from === state.channelOwner && msg.from !== state.user?.id) {
			if (state.sharingCam || state.sharingScreen) webRTC.stopVideoShare();
		}
	} else if (msg.type === 'host_mute_user') {
		if (msg.from === state.channelOwner && (msg as any).user_id === state.user?.id) {
			state.enableMic(false);
		}
	} else if (msg.type === 'host_stop_user_video') {
		if (msg.from === state.channelOwner && (msg as any).user_id === state.user?.id) {
			if (state.sharingCam || state.sharingScreen) webRTC.stopVideoShare();
		}
	} else if (msg.type === 'host_kick_user') {
		if (msg.from === state.channelOwner && (msg as any).user_id === state.user?.id) {
			state.hangup();
		}
	}
}
